import React, { useCallback, useEffect, useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Pagination,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Stack,
} from '@mui/material';

import PageHeader from '../../components/PageHeader';
import {
  permissionsList,
  permissionsWithAssignedIds,
} from '../../api/constant';
import { toast } from 'sonner';
import UIModal from '../../components/UIElements/UIModal';
import ProtectedComponent from '../../common/ProtectedComponent';
import {
  deleteNotification,
  getNotificationDays,
  getNotifications,
  sendNotification,
  updateNotificationDays,
} from '../../api/notificationsApi';
import DeleteModal from './DeleteModal';
import { Link } from 'react-router-dom';
import SemenFreezingTable from './SemenFreezingTable';
import EmbryoFreezingTable from './EmbryoFreezingTable';

const emptyModalState = {
  open: false,
  title: '',
  body: '',
  proceedText: '',
  cancelText: '',
  onSave: null,
  onClose: null,
  needsComments: false,
};

export default function Notifications() {
  const [modalOpen, setModalOpen] = useState({
    open: false,
    title: '',
    body: '',
    proceedText: '',
    cancelText: '',
    onSave: null,
    onClose: null,
    needsComments: false,
  });
  const [notificationDays, setNotificationDays] = useState(0);
  const [notifications, setNotifcations] = useState({
    notifications: [],
    totalNotifications: 0,
    totalPages: 0,
  });
  const [page, setPage] = useState({
    page: 1,
    pageSize: 10,
  });

  const [activeTab, setActiveTab] = useState(0);

  const getCellData = (data, key) => {
    if (key === 'patientName') {
      const firstName =
        data['patient.firstName'] === '' ? 'N/A' : data['patient.firstName'];
      const lastName =
        data['patient.lastName'] === '' ? 'N/A' : data['patient.lastName'];
      return `${firstName} ${lastName}`;
    }

    if (key === 'spouseName') {
      const firstName =
        data['patient.partner.firstName'] === ''
          ? 'N/A'
          : data['patient.partner.firstName'];
      const lastName =
        data['patient.partner.lastName'] === ''
          ? 'N/A'
          : data['patient.partner.lastName'];
      return `${firstName} ${lastName}`;
    }

    if (key.includes('cryopreservation')) {
      const field = key.split('.')[1];
      if (
        data.cryopreservation === null ||
        data.cryopreservation === undefined ||
        data.cryopreservation[field] === null ||
        data.cryopreservation[field] === ''
      ) {
        return 'N/A';
      }

      if (field === 'freezingDuration') {
        const years =
          data.cryopreservation[field].years === ''
            ? 0
            : data.cryopreservation[field].years;
        const months =
          data.cryopreservation[field].months === ''
            ? 0
            : data.cryopreservation[field].months;

        let duration = '';

        if (years) {
          duration += `${years} years `;
        }

        if (months) {
          duration += `${months} months `;
        }

        return duration === '' ? 'N/A' : duration;
      }
      return data.cryopreservation[field];
    }

    if (key.includes('embryo')) {
      const field = key.split('.')[1];

      if (field === 'freezingDuration') {
        if (
          data.freezingInventory === null ||
          data.freezingInventory === undefined
        ) {
          return 'N/A';
        }
        const years =
          data.freezingInventory.freezingDurationYears === ''
            ? 0
            : data.freezingInventory.freezingDurationYears;
        const months =
          data.freezingInventory.freezingDurationMonths === ''
            ? 0
            : data.freezingInventory.freezingDurationMonthsv;

        let duration = '';

        if (years) {
          duration += `${years} years `;
        }

        if (months) {
          duration += `${months} months `;
        }

        return duration === '' ? 'N/A' : duration;
      }
      if (
        data.freezingInventory === null ||
        data.freezingInventory === undefined ||
        data.freezingInventory[field] === null ||
        data.freezingInventory[field] === ''
      ) {
        return 'N/A';
      }
      return data.freezingInventory[field];
    }

    if (data[key] === null || data[key] === undefined || data[key] === '') {
      return 'N/A';
    }

    if (key === 'lastNotificationSentAt') {
      return new Date(data[key]).toLocaleString('en-IN');
    }
    return data[key];
  };

  const fetchDaysForNotification = useCallback(() => {
    toast.promise(
      getNotificationDays().then((response) => {
        setNotificationDays(response.data.payload.sfrExpiryDays);
      }),
      {
        loading: 'Loading notification days...',
        success: 'Notification days loaded',
        error: 'Failed to load notification days',
      },
    );
  }, []);

  const handleUpdateNotificationDays = (days) => {
    if (days === null || days === undefined || days === '') {
      toast.error('Please enter a valid number of days');
      return;
    }

    if (days <= 0) {
      toast.error('Please enter a valid number of days');
      return;
    }
    toast.promise(
      updateNotificationDays(days).then((response) => {
        console.log(response);
        setNotificationDays(days);
        setModalOpen({ ...emptyModalState });
      }),
      {
        loading: 'Updating notification days...',
        success: 'Notification days updated',
        error: 'Failed to update notification days',
      },
    );
  };

  const fetchNotifications = useCallback(() => {
    toast.promise(
      getNotifications(
        page.page,
        page.pageSize,
        activeTab === 0 ? 'semen' : 'embryo',
      ).then((response) => {
        console.log(response);
        setNotifcations({
          notifications: response.data.payload.notifications,
          totalNotifications: response.data.payload.totalRecords,
          totalPages: response.data.payload.totalPages,
        });
      }),
      {
        loading: 'Loading notifications...',
        success: 'Notifications loaded',
        error: 'Failed to load notifications',
      },
    );
  }, [page.page, page.pageSize, activeTab]);

  const handleResendNotification = (notification) => {
    toast.promise(
      sendNotification(
        notification.patientId,
        notification.id,
        activeTab === 0 ? 'semen' : 'embryo',
      )
        .then((response) => {
          toast.success('Notification sent successfully');
          setModalOpen({ ...emptyModalState });
        })
        .then(() => fetchNotifications()),
      {
        loading: 'Sending notification...',
        success: 'Notification sent successfully',
        error: 'Failed to send notification',
      },
    );
  };

  const handleDeleteNotification = (notification, deleteComment) => {
    if (deleteComment === '' || deleteComment === null) {
      toast.error('Please enter a reason for deletion');
      return;
    }
    toast.promise(
      deleteNotification(
        notification.patientId,
        notification.id,
        activeTab === 0 ? 'semen' : 'embryo',
        deleteComment,
      )
        .then((response) => {
          setModalOpen({ ...emptyModalState });
        })
        .then(() => fetchNotifications()),
      {
        loading: 'Deleting notification...',
        success: 'Notification deleted successfully',
        error: 'Failed to delete notification',
      },
    );
  };

  useEffect(() => {
    fetchNotifications();
  }, [page, fetchNotifications, activeTab]);

  useEffect(() => {
    fetchDaysForNotification();
  }, [fetchDaysForNotification]);

  const handlePageChange = (event, value) => {
    setPage({ ...page, page: value });
  };

  const handlePageSizeChange = (event) => {
    setPage({ ...page, pageSize: event.target.value });
  };

  return (
    <ProtectedComponent
      requiredPermission={
        permissionsWithAssignedIds[permissionsList.FreezingNotifications]
      }
    >
      {modalOpen.needsComments ? (
        <DeleteModal modalOpen={modalOpen} />
      ) : (
        <UIModal
          title={modalOpen.title}
          body={modalOpen.body}
          proceedText={modalOpen.proceedText}
          cancelText={modalOpen.cancelText}
          show={modalOpen.open}
          onClose={modalOpen.onClose}
          onSave={modalOpen.onSave}
        />
      )}
      <div className=" container-fluid">
        <div style={{ width: '100%' }} className="theme-blush ">
          <PageHeader
            HeaderText="View Notifications"
            Breadcrumb={[
              { name: 'View Notifications', navigate: '/notifications' },
            ]}
          />

          <div
            className="flex"
            style={{ alignItems: 'center', height: '100%' }}
          >
            Current Notification Days : {notificationDays}
            <IconButton
              onClick={() => {
                setModalOpen({
                  open: true,
                  title: 'Update Notification Days',
                  body: 'Enter days (greater than 0)',
                  proceedText: 'Update',
                  cancelText: 'Cancel',
                  needsComments: true,
                  isNumber: true,
                  existingMessage: notificationDays,
                  onSave: (days) => {
                    handleUpdateNotificationDays(days);
                  },
                  onClose: () => {
                    setModalOpen({ ...emptyModalState });
                  },
                });
              }}
              color="primary"
              aria-label="update"
            >
              <i className="fa fa-pencil"></i>
            </IconButton>
          </div>

          <ul
            className="nav nav-tabs"
            role="tablist"
            style={{
              background: '#ffff',
            }}
          >
            <li
              className={`nav-item mr-1 ${activeTab === 0 ? 'active' : ''}`}
              onClick={() => setActiveTab(0)}
            >
              <Link
                to="#"
                className={`nav-link ${activeTab === 0 ? 'active' : ''}`}
              >
                Semen Freezing
              </Link>
            </li>

            <li
              className={`nav-item mr-1 ${activeTab === 1 ? 'active' : ''}`}
              onClick={() => setActiveTab(1)}
            >
              <Link
                to="#"
                className={`nav-link ${activeTab === 1 ? 'active' : ''}`}
              >
                Embryo Freezing
              </Link>
            </li>
          </ul>

          <div className="tab-content component-wrapper">
            {activeTab === 0 && (
              <SemenFreezingTable
                notifications={notifications}
                setModalOpen={setModalOpen}
                getCellData={getCellData}
                handleResendNotification={handleResendNotification}
                handleDeleteNotification={handleDeleteNotification}
                emptyModalState={emptyModalState}
              />
            )}

            {activeTab === 1 && (
              <EmbryoFreezingTable
                notifications={notifications}
                setModalOpen={setModalOpen}
                getCellData={getCellData}
                handleResendNotification={handleResendNotification}
                handleDeleteNotification={handleDeleteNotification}
                emptyModalState={emptyModalState}
              />
            )}

            <Pagination
              count={notifications.totalPages}
              page={page.page}
              onChange={handlePageChange}
              color="primary"
              style={{
                marginTop: '20px',
                display: 'flex',
                justifyContent: 'center',
              }}
            />
            <FormControl
              variant="outlined"
              style={{ minWidth: 120, marginBottom: '20px' }}
            >
              <InputLabel id="page-size-label">Page Size</InputLabel>
              <Select
                labelId="page-size-label"
                id="page-size-select"
                value={page.pageSize}
                onChange={handlePageSizeChange}
                label="Page Size"
              >
                <MenuItem value={5}>5</MenuItem>
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={15}>15</MenuItem>
                <MenuItem value={20}>20</MenuItem>
              </Select>
            </FormControl>
          </div>
        </div>
      </div>
    </ProtectedComponent>
  );
}
